import { currentToken, useSessionStore } from '@/stores/session'

export const API_URL: string = (import.meta.env.VITE_API_URL as string) ?? ''

/** Ein Fehler, den die Oberfläche der Schülerin zeigen kann. */
export class ApiError extends Error {
    constructor(
        public readonly status: number,
        message: string,
        public readonly errors: Record<string, string[]> = {},
    ) {
        super(message)
        this.name = 'ApiError'
    }

    /** Erste Meldung zu einem Feld, für die Anzeige unter dem Eingabefeld. */
    fieldError(field: string): string | undefined {
        return this.errors[field]?.[0]
    }
}

interface RequestOptions {
    method?: 'GET' | 'POST'
    body?: unknown
    /** Ohne Token aufrufen - nur der Beitritt braucht das. */
    anonymous?: boolean
}

/**
 * Ruft die REST-API der laravel-app auf.
 *
 * Client → Server läuft ausschließlich über diese Endpunkte; Antworten in die
 * andere Richtung kommen als Broadcast. Hier wird also nur gesendet und
 * abgefragt, nie abonniert.
 */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, anonymous = false } = options
    const token = anonymous ? null : currentToken()

    const response = await fetch(`${API_URL}/api${path}`, {
        method,
        headers: {
            Accept: 'application/json',
            ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
            ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
        },
        body: body === undefined ? undefined : JSON.stringify(body),
    })

    if (response.status === 401 && !anonymous) {
        // Das Token gilt nicht mehr - etwa weil die Runde beendet wurde. Der
        // gespeicherte Beitritt wäre ab jetzt nur noch im Weg.
        useSessionStore.getState().leave()
    }

    const payload: unknown =
        response.status === 204 ? null : await response.json().catch(() => null)

    if (!response.ok) {
        throw new ApiError(
            response.status,
            readMessage(payload) ?? `HTTP ${response.status}`,
            readErrors(payload),
        )
    }

    return payload as T
}

function readMessage(payload: unknown): string | undefined {
    if (payload !== null && typeof payload === 'object') {
        const record = payload as Record<string, unknown>
        const message = record.message ?? record.detail

        if (typeof message === 'string' && message !== '') {
            return message
        }
    }

    return undefined
}

function readErrors(payload: unknown): Record<string, string[]> {
    if (payload !== null && typeof payload === 'object') {
        const errors = (payload as Record<string, unknown>).errors

        if (errors !== null && typeof errors === 'object') {
            return errors as Record<string, string[]>
        }
    }

    return {}
}
